// Waste namespace — RUSSIAN (Module 05 §4). The design partner is a cable
// factory in Almaty (docs/context/kazakhstan-group.md) whose group_admin
// configures this screen, and whose floor taps the categories it defines. An
// English waste-type list produces Russian-language waste reports against
// English-language buttons; this file is what stops that.
//
// Typed `Translated<typeof wasteEn>` (src/i18n-shape.ts), so a key added to or
// removed from `wasteEn` fails `tsc` here. Values are checked separately, at
// runtime, by tools/ci/i18n-parity.ts.
//
// ── REGISTER ────────────────────────────────────────────────────────────────
// Shop-floor and accounting Russian, not calque-from-English. The choices worth
// arguing with in one place:
//
//  · waste type   — «вид отходов». NOT «категория»: a Russian factory's
//                   paperwork says «виды отходов», and the floor button is
//                   read at arm's length.
//  · reason       — «причина». Plain, and the same word the downtime and defect
//                   catalogues use, so the three floor grids read alike.
//  · off-cut      — «обрезок» (a cut-off piece), «остаток» for a usable
//                   remainder on a drum. The two are different things on a
//                   cable line and the pack labels already distinguish them.
//  · disposition  — «дальнейшая судьба» reads like a novel; the column is
//                   «что с этим делают» — what is normally DONE with it, which
//                   is what `default_disposition` actually means.
//  · regrind      — «дробление» (the PVC loop's own word), not «переработка»,
//                   which is the whole recycling process rather than this step.
//  · hold         — «резерв» (see the inventory register note: «блокировка» is
//                   the quality hold, a different thing).
//  · tolerance    — «допуск», the metrology word, everywhere.
//
// ── WHAT IS NOT HERE ────────────────────────────────────────────────────────
// The names of the waste types and reasons themselves. Those are TENANT DATA
// (`name_i18n` per row, seeded from the pack in all four locales), which is
// precisely why the editor shows one input per locale instead of one input.

import type { WasteDict } from "./waste.i18n.js";

export const wasteRuDict: WasteDict = {
  nav: {
    aria: "Разделы отходов",
    settings: "Настройки отходов",
    ledger: "Журнал отходов",
    shifts: "Табло по сменам",
    pareto: "Парето по браку",
    remnants: "Остатки",
  },

  common: {
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    // Заголовок problem-ответа приходит по проводу по-английски (D-022) — эта
    // подпись объясняет, чьи это слова, чтобы английская фраза на русском
    // экране не читалась как наша сломанная строка.
    serverSaid: "Отклонено: «{{title}}»",
    save: "Сохранить",
    saving: "Сохранение…",
    cancel: "Отмена",
    edit: "Изменить",
    create: "Создать",
    activate: "Включить",
    deactivate: "Выключить",
    close: "Закрыть",
    loading: "Загрузка…",
    prev: "Назад",
    next: "Вперёд",
  },

  access: {
    title: "Этот экран не для вашей роли",
    area: "Вашей роли не назначено ни одного экрана по отходам. Обратитесь к администратору группы, если они вам нужны.",
    ledger:
      "Ваша роль не читает журнал отходов. Администратор группы может выдать доступ, если это входит в ваши обязанности.",
    registry:
      "Ваша роль не ведёт реестр остатков. Администратор группы может выдать доступ, если это входит в ваши обязанности.",
  },

  offline: {
    title: "Нет сети",
    body: "Устройство сейчас без сети. Экран заполнится сам, как только связь вернётся — ничего не потеряно.",
  },

  source: {
    floor_report: "С участка",
    qc_disposition: "Решение ОТК",
    calibration_variance: "Расхождение по счётчику",
    cut_offcut: "Резка / обрезок",
    count_variance: "Расхождение при пересчёте",
    byproduct_output: "Побочный продукт",
    regrind_loss: "Потери при дроблении",
    manual: "Ввод из офиса",
  },

  remnantKind: {
    wire_remnant: "Остаток провода",
    cable_offcut: "Обрезок кабеля",
    sheet_offcut: "Обрезок листа",
  },
  remnantStatus: {
    available: "Свободен",
    reserved: "В резерве",
    consumed: "Израсходован",
    sold: "Продан",
    scrapped: "Списан",
  },

  events: {
    title: "Журнал отходов",
    filters: "Фильтры ({{n}})",
    filtersTitle: "Фильтры",
    filtersDone: "Готово",
    filterRemove: "Убрать: {{label}}",
    exportCsv: "Выгрузить CSV",
    // Голос полной выгрузки (аудит GM-недели): ход выгрузки, честное
    // предупреждение об усечённом файле и обещание «файла нет», если
    // выгрузка оборвалась. «Выгружено строк: {{n}}» — форма с двоеточием
    // сознательно: она не требует согласования числительного.
    exporting: "Выгрузка… строк: {{n}}",
    exportCapped:
      "Выгружены только первые строки ({{n}}) — в журнале есть ещё. Сузьте диапазон дат и выгрузите остальное отдельно.",
    exportFailed: "Не удалось выгрузить журнал — файл не сохранён.",
    facility: "Площадка",
    allFacilities: "Все площадки",
    from: "С",
    to: "По",
    category: "Вид отходов",
    allCategories: "Все виды отходов",
    allCauses: "Все причины",
    source: "Источник",
    allSources: "Все источники",
    disposition: "Что с этим делают",
    allDispositions: "Любое решение",
    posted: "Проведено",
    postedAny: "Проведено или нет",
    postedYes: "Проведено",
    postedNo: "Не проведено",
    // 05-B-2 — фильтры «рабочий центр / смена / заказ-наряд» экрана S2.
    workCenter: "Рабочий центр",
    allWorkCenters: "Все рабочие центры",
    shift: "Смена",
    allShifts: "Любая смена",
    pickFacilityFirst: "Сначала выберите площадку",
    wo: "Заказ-наряд",
    woPlaceholder: "Вставьте идентификатор заказ-наряда",
    woInvalid: "Это не идентификатор заказ-наряда — фильтр не применяется, пока он не станет им.",
    error: "Не удалось загрузить журнал отходов",
    emptyTitle: "Событий отходов пока нет",
    emptyBody: "Отходы появляются здесь, как только их зафиксирует участок, ОТК или машина.",
    tableCaption: "События отходов, новые сверху",
    colNo: "№",
    colWhen: "Когда",
    colCategory: "Вид отходов",
    colCause: "Причина",
    colQty: "Кол-во",
    colCost: "Стоимость",
    colSource: "Источник",
    colDisposition: "Решение",
    reversedChip: "Сторнировано",
    reversalChip: "Сторно",
    loadMore: "Показать ещё",
    drawerShift: "Смена",
    noShift: "Без смены — ввод из офиса",
    drawerOrigin: "Смена-виновник",
    handedOffFromShift: "Передано со смены {{shift}}",
    handedOffFrom: "Передано с другой смены",
    drawerWo: "Заказ-наряд",
    drawerPhotos: "Фото",
    drawerNotes: "Примечания",
    drawerCorrection: "Корректировка",
    correctionRestatement: "Пересчитано из {{eventNo}}",
    correctionAddition: "Добавлено к {{eventNo}}",
    decideLabel: "Решите, что с этим делать",
    decideSave: "Записать решение",
    decideFailed: "Не удалось записать решение — возможно, оно уже принято.",
    reverseReason: "Что здесь неверно?",
    reverseConfirm: "Создать сторно",
    reverseOpen: "Сторнировать…",
    reverseFailed: "Не удалось сторнировать событие.",
    reversedNote:
      "Это событие входит в пару сторно. Пара обнуляется во всех итогах; для исправленного факта запишите новое событие.",
  },

  shifts: {
    title: "Табло отходов по сменам",
    kioskBadge: "Режим табло — обновляется каждую минуту",
    date: "Дата",
    error: "Не удалось загрузить табло",
    emptyTitle: "В этот день отходов нет",
    emptyBody: "По этой площадке и дате ничего не зафиксировано.",
    unattributed: "Без смены",
    unattributedNote:
      "Офисные и задним числом введённые записи намеренно не относятся ни к одной смене — деньги, внесённые днями позже, не должны лечь на тех, кто случайно работал в тот момент.",
    cost: "Стоимость отходов",
    events: "Событий",
    handedOff: "Передано с другой смены",
    topCategories: "Главные виды отходов за день",
    week: "Последние 7 дней",
    weekNone: "Нет",
    // D-030 (the house rule stated in inventory.ru.ts): no counted noun is ever
    // agreed with its number here — «{{count}} событий» renders "1 событий".
    weekEvents: "События: {{count}}",
    drillLedger: "Открыть эти события в журнале",
    drillShift: "Открыть эту смену в журнале",
    kioskContext: "{{facility}} · {{date}}",
    kioskFacilityUnknown: "Площадка не указана",
  },

  // S13 — «Парето по браку». Слова, которые стоит защитить:
  //  · Парето — так и говорят на производстве; «диаграмма Парето» — узнаваемый
  //    термин у любого технолога, прошедшего систему качества.
  //  · «причина» — то же слово, что в каталоге причин и в журнале, чтобы три
  //    экрана читались одинаково.
  //  · «нарастающим итогом» — бухгалтерская формула, ровно то, что считает
  //    столбец; «накопительно» звучит как калька.
  //  · «за период» вместо «за месяц»: диапазон выбирает пользователь.
  pareto: {
    title: "Парето по браку",
    intro: "Отходы по стоимости. Важнее всего причины выше 80%.",
    groupBy: "Ранжировать по",
    byCause: "Причине",
    byCategory: "Виду отходов",
    from: "С",
    to: "По",
    presetThisMonth: "Текущий месяц",
    presetLastMonth: "Прошлый месяц",
    rangeError: "Начальная дата позже конечной — период не выбран.",
    error: "Не удалось загрузить Парето",
    emptyTitle: "За этот период отходов нет",
    emptyBody: "По этой площадке между указанными датами ничего не зафиксировано.",
    basisMoney: "По стоимости ({{currency}})",
    basisQty: "По количеству ({{uom}})",
    redactedNote:
      "Ваша роль не видит стоимость отходов, поэтому список построен по количеству, отдельно по каждой единице.",
    colRank: "№",
    colCause: "Причина",
    colCategory: "Вид отходов",
    colCost: "Стоимость",
    colQty: "Количество",
    colShare: "Доля",
    colCumulative: "Нарастающим итогом",
    colEvents: "Событий",
    colVolume: "Объём",
    colDrill: "Журнал",
    drill: "Открыть в журнале",
    tableCaption: "Причины отходов по убыванию с долей каждой за период",
    noCause: "Причина не указана",
    noCategory: "Вид отходов не указан",
    shareUnknown: "—",
    zeroCost: "Стоимость этих отходов не зафиксирована, поэтому ранжировать их по деньгам нельзя.",
    zeroQty: "По этим отходам не зафиксировано количество.",
    // D-030: считаемое существительное не согласуется с числом.
    vitalFew: "Первые позиции ({{count}}) дают 80% за период.",
  },

  remnants: {
    title: "Реестр остатков",
    search: "Поиск",
    searchHint: "Номер остатка…",
    // 05-B-2 — половина строки поиска «изделие + нужная длина» из §4.
    item: "Изделие",
    itemHint: "Введите полный артикул…",
    itemApplied: "Фильтр по {{sku}}",
    itemNoMatch: "Изделия с таким артикулом нет — фильтр не применяется, пока артикул не совпадёт.",
    kind: "Тип",
    anyKind: "Любой тип",
    status: "Статус",
    anyStatus: "Любой статус",
    minLength: "Длина от (м)",
    maxLength: "Длина до (м)",
    error: "Не удалось загрузить реестр остатков",
    emptyTitle: "Остатков на учёте нет",
    emptyBody: "Обрезки появляются здесь при резке или когда участок регистрирует остаток.",
    tableCaption: "Остатки, сначала самые длинные",
    colNo: "№",
    colKind: "Тип",
    colLength: "Длина",
    colDiameter: "Ø",
    colClass: "Ценовой класс",
    colStatus: "Статус",
    colAge: "Зарегистрирован",
    colActions: "Действия",
    unclassed: "Без класса",
    actions: "Действия",
    // Отказы ПО ГЛАГОЛАМ (task #105): одна фраза «действие отклонено» на
    // четыре разных действия не говорила, какое именно отклонено и почему.
    reserveFailed: "Не удалось поставить кусок в резерв — возможно, его уже забрали.",
    releaseFailed: "Не удалось снять резерв — возможно, его уже нет.",
    reclassFailed: "Не удалось сменить класс куска.",
    scrapFailed: "Не удалось списать кусок.",
    reserve: "В резерв",
    reserveFor: "Резерв под",
    reserveWo: "Заказ-наряд",
    reserveSo: "Строку заказа",
    reserveRef: "Идентификатор",
    release: "Снять резерв",
    reclass: "Сменить класс",
    reclassPick: "Выберите класс",
    reclassTo: "Новый ценовой класс",
    scrap: "Списать",
    scrapReason: "Почему списываем?",
  },

  floor: {
    captureTitle: "Записать отходы",
    remnantTitle: "Зарегистрировать остаток",
    backToStation: "Назад к станции",
    back: "Назад",
    noCatalogTitle: "На этом устройстве ещё нет видов отходов",
    noCatalogBody:
      "Откройте этот экран один раз при подключении к сети — списки сохранятся для работы офлайн.",
    noJobTitle: "На этой станции нет задания",
    noJobBody:
      "Отходы записываются на задание, при котором они возникли. Когда задание появится в очереди станции, оно отобразится выше.",
    qtyLabel: "Сколько? ({{uom}})",
    lengthLabel: "Длина (м)",
    diameterLabel: "Диаметр (мм) — не обязательно",
    locationLabel: "Куда положить кусок?",
    locationPick: "Выберите ячейку",
    noteLabel: "Примечание — не обязательно",
    addPhoto: "Добавить фото",
    photosPicked: "Прикреплено фото: {{n}}",
    photoRefused: "Это фото не подходит — слишком большое или не изображение.",
    saveCapture: "Сохранить отходы",
    saveRemnant: "Зарегистрировать кусок",
    saving: "Сохранение…",
    captureAnother: "Записать ещё",
    remnantExistingHint: "У куска уже есть этикетка?",
    remnantScanLink: "Отсканируйте её, чтобы изменить длину или место.",
    savedApplied: "Сохранено.",
    savedQueued: "Сохранено на устройстве — отправится, когда появится сеть.",
    savedConflicted: "Это расходится с уже записанным — нужна проверка мастером.",
    savedFailed: "Не сохранено: запись отклонена. Сообщите мастеру.",
    savedSuperseded: "Уже записано в другом месте.",
  },

  locale: {
    en: "Английский",
    ru: "Русский",
    fr: "Французский",
    zh: "Китайский (упрощённый)",
    "zh-Hant": "Китайский (традиционный)",
    hi: "Хинди",
  },

  disposition: {
    pending: "Решение не принято",
    reuse: "Повторное использование",
    regrind: "Дробление",
    sell: "Продажа",
    dispose: "Утилизация",
  },

  settings: {
    title: "Настройки отходов",
    intro: "Из чего выбирает цех, когда записывает отходы.",
    readOnly: "Эти настройки доступны для просмотра. Изменять их может администратор группы.",
    settingsReadOnly: "Изменять эти числа может только администратор группы.",
    tabsAria: "Разделы настроек отходов",
    tab: {
      categories: "Виды отходов",
      causes: "Причины",
      classes: "Классы цен на обрезки",
      matcher: "Подбор и допуски",
    },
    loading: "Загрузка…",
    saved: "Сохранено.",

    colCode: "Код",
    colName: "Название",
    colDisposition: "Что с этим обычно делают",
    colFlags: "Что спрашивать",
    colOwner: "Источник",
    colActions: "Действия",
    colAppliesTo: "Применяется к",
    colBlame: "Указывает на",
    colDiscount: "Скидка",
    colBand: "Диапазон длины",
    colSortOrder: "Порядок",
    colFloorPolicy: "Продажа ниже себестоимости",

    fromPack: "Отраслевой пакет",
    fromPlatform: "Встроено",
    tenantOwned: "Ваше",
    customize: "Изменить под себя",
    rowMenu: "Другие действия: {{code}}",
    otherLanguages: "Другие языки",
    search: "Поиск видов отходов и причин",
    searchPlaceholder: "Код или название",
    noMatch: "По этому запросу ничего нет.",
    pageRange: "Ваши: {{from}}–{{to}} из {{total}}",
    customizeHint:
      "Эту строку поддерживает отраслевой пакет. После изменения она становится вашей, и обновления пакета её больше не трогают.",
    noTranslation: "Английский",
    noTranslationHint: "Перевода на ваш язык пока нет — показано английское название.",
    optionalLocale: "Оставьте пустым, если перевода нет",
    codeIsForever:
      "Код постоянный — на него ссылаются отчёты цеха о браке. Название можно менять когда угодно.",

    flagDimensions: "Спрашивать длину",
    flagNoDimensions: "Без длины",
    flagCause: "Спрашивать причину",
    flagNoCause: "Без причины",
    flagRecoverable: "Подлежит возврату",
    flagNotRecoverable: "Чистая потеря",
    allCategories: "Все виды отходов",
    blamesMachine: "На станок",
    blamesSupplier: "На поставщика",
    blamesNobody: "Ни на что конкретно",

    sortOrderHint:
      "Меньшие числа показываются в цехе первыми. Оставьте пустым, чтобы порядок не менялся.",
    sortOrderInvalid: "Порядок — целое число от 0 до 32767.",

    appliesToAllHint: "Ничего не отмечено — причина доступна для всех видов отходов.",
    appliesToHint:
      "Причина будет предлагаться только для отмеченных видов отходов. Снимите все отметки, чтобы разрешить её везде.",

    floorPolicy: {
      above_cost: "Нужно согласование",
      none: "Разрешено",
    },
    floorPolicyHint:
      "«Нужно согласование» — продать кусок дешевле его себестоимости можно только после согласования.",
    deactivateFreesBand:
      "Два класса не могут занимать одну и ту же длину. Если класс выключить, его диапазон освобождается.",

    addCategory: "Добавить вид отходов",
    addCause: "Добавить причину",
    addClass: "Добавить класс цены",

    bandHint:
      "Цена куска определяется диапазоном, в который попадает его длина. Диапазоны не должны пересекаться: у обрезка 100 м должна быть ровно одна цена.",
    bandNone: "Назначается вручную",
    bandBelow: "Менее {{max}} м",
    bandAbove: "От {{min}} м",
    bandRange: "{{min}}–{{max}} м",
    bandMin: "От (м)",
    bandMax: "До, не включая (м)",
    pct: "{{value}}%",

    emptyCategoriesTitle: "Видов отходов пока нет",
    emptyCategoriesBody:
      "Вид отходов — первое, что нажимает оператор при списании. Установите отраслевой пакет или добавьте свои.",
    emptyCausesTitle: "Причин пока нет",
    emptyCausesBody:
      "Причина — второе нажатие в цеху: почему это произошло. Добавьте те, что реально встречаются на линии.",
    emptyClassesTitle: "Классов цен пока нет",
    emptyClassesBody:
      "Классы цен задают скидку на обрезки при продаже. Добавьте по одному на каждый диапазон длины со своей ценой.",

    errorCatalog: "Не удалось загрузить списки отходов",
    errorClasses: "Не удалось загрузить классы цен",
    errorSettings: "Не удалось загрузить настройки",
    noSettingsAccessTitle: "Эти настройки вам недоступны",
    noSettingsAccessBody: "Допуски и параметры подбора видят администраторы и планировщики.",

    field: {
      exact_tolerance_m: "Допуск точного совпадения (м)",
      max_overage_pct: "Допустимый перерасход куска (%)",
      reservation_ttl_hours: "Срок действия резерва (часов)",
      variance_tolerance_default_pct: "Превышение плана по отходам до сигнала (%)",
      wire_diameter_tolerance_mm: "Допуск «та же жила» (мм)",
      min_remnant_track_length_m: "Минимальная длина, которую стоит хранить (м)",
      recovery_target_pct_default: "Цель по возврату по умолчанию (%)",
    },
    fieldHint: {
      exact_tolerance_m:
        "Кусок, отличающийся от запрошенной длины не более чем на это значение, считается точным совпадением.",
      max_overage_pct: "Предложение, в котором отход больше этого значения, не показывается.",
      reservation_ttl_hours: "По истечении срока кусок сам возвращается на склад.",
      variance_tolerance_default_pct: "Применяется, если отдельная цель не задаёт иное.",
      wire_diameter_tolerance_mm: "Две жилы в пределах этого допуска считаются одинаковыми.",
      min_remnant_track_length_m:
        "Более короткие обрезки — это отходы, а не остаток. Из отраслевого пакета.",
      recovery_target_pct_default:
        "Доля отходов, которую планируется вернуть. Из отраслевого пакета.",
    },
    packDefaults: {
      pristine:
        "Последние два значения пришли из отраслевого пакета, и обновления пакета их пока корректируют. Измените любое из них — и это прекратится навсегда.",
      customized:
        "Для одного из двух значений пакета задано собственное. Обновления пакета их больше не меняют.",
    },
  },
};
